import React from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { Menu, Home } from 'lucide-react';
import { useUIStore } from '../../store/uiStore';
import { useChatStore } from '../../store/chatStore';
import { useSkillsStore } from '../../store/skillsStore';
import { useAuthStore } from '../../store/authStore';
import Sidebar from './Sidebar';
import Button from '../ui/Button';
import SkillSelector from '../ui/SkillSelector';
import config from '../../config';
import UserMenu from '../ui/UserMenu';
import logoSmall from '../../assets/images/logo_64x64.png';
import textLogo from '../../assets/images/text_logo_image.png';

const AppLayout: React.FC = () => {
  const location = useLocation();
  const {
    sidebarOpen,
    isMobile,
    toggleSidebar,
    setIsMobile,
  } = useUIStore();
  
  const { currentThread, updateThreadSkill } = useChatStore();
  const { skills, fetchSkills } = useSkillsStore();
  const { user } = useAuthStore();

  // Check if we're on a chat page
  const isChatPage = location.pathname.startsWith('/chat') || location.pathname.startsWith('/folder');

  // Fetch skills on mount
  React.useEffect(() => {
    if (skills.length === 0) {
      fetchSkills();
    }
  }, [skills.length, fetchSkills]);

  // Handle skill change
  const handleSkillChange = async (skillId: string) => {
    if (!currentThread) return;
    
    try {
      await updateThreadSkill(currentThread.id, skillId);
      // Update local thread state directly without triggering message reload
      useChatStore.setState((state) => ({
        currentThread: state.currentThread 
          ? { ...state.currentThread, skill_id: skillId }
          : null
      }));
    } catch (error) {
      console.error('Failed to update skill:', error);
    }
  };

  // Resolve current skill_id: Thread → User default → "default"
  const currentSkillId = currentThread?.skill_id || user?.default_skill_id || 'default';

  // Handle responsive behavior
  React.useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [setIsMobile]);

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar - z-40 so it's above content but below some modals */}
      <div
        className={`fixed inset-y-0 left-0 z-40 bg-white shadow-xl transition-transform duration-300 ease-in-out ${
          sidebarOpen 
            ? 'translate-x-0 w-80' 
            : '-translate-x-full'
        } ${isMobile ? 'w-80' : ''}`}
      >
        {sidebarOpen && <Sidebar />}
      </div>

      {/* Mobile overlay */}
      {isMobile && sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 transition-opacity"
          onClick={toggleSidebar}
        />
      )}

      {/* Main content area - Push content over when sidebar is visible (desktop only). min-w-0 + overflow-x-hidden so header never scrolls into sidebar. */}
      <div 
        className={`flex-1 flex flex-col min-w-0 overflow-x-hidden transition-all duration-300 ${
          sidebarOpen && !isMobile ? 'ml-80' : 'ml-0'
        }`}
      >
        {/* Header */}
        <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between sticky top-0 z-50 flex-shrink-0">
          <div className="flex items-center space-x-3 flex-1">
            <Button
              variant="ghost"
              size="sm"
              icon={Menu}
              onClick={toggleSidebar}
            />
            <Link to="/" className="flex items-center space-x-2 hover:opacity-80 transition-opacity">
              <img src={logoSmall} alt={`${config.appName} Logo`} className="w-6 h-6" />
              <img src={textLogo} alt={`${config.appName}`} className="h-5 w-auto hidden sm:block" />
            </Link>
          </div>

          <div className="flex items-center space-x-2">
            {/* Skill Selector - only show on chat pages */}
            {isChatPage && skills.length > 0 && (
              <SkillSelector
                skills={skills}
                selectedSkillId={currentSkillId}
                onSkillChange={handleSkillChange}
                disabled={!currentThread}
                className="mr-2"
              />
            )}
            <Link
              to="/"
              className="p-2 text-gray-600 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Go to Home"
            >
              <Home className="h-5 w-5" />
            </Link>
            <UserMenu />
          </div>
        </header>

        {/* Main content - vertical scroll only; no horizontal scroll so header stays fixed */}
        <div className="flex-1 flex min-h-0 min-w-0">
          <main className="flex-1 min-h-0 min-w-0 overflow-x-hidden overflow-y-auto">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
};

export default AppLayout;
